import { flatMap, get, pickBy } from 'lodash'
import type {
  DigestedObjectFieldWithFields,
  DigestedSchema,
  DigestedSchemaField,
} from '@wix/wix-data-client-common-standalone'
import transformToClientAllowedOperations from '../../transformToClientAllowedOperations'
import {
  CollectionType,
  getCollectionType,
} from '@wix/wix-code-collections-api'
import { isSinglesCollectionSchema } from '../../collections/collectionTypes'
import { deserializeDefaultSort } from '../sort/sortUtils'
import type { FieldSpec, Schema } from '@wix/wix-data-schema-types'
import { isDbDriverCollection } from './collectionTypes'
import { isOfObjectTypeWithFields } from '../../utils'

const hasLegalFieldname = ({ name }: { name: string }): boolean =>
  !name.includes('.')

const systemFieldsLast = (rawFields: Schema['fields']): Schema['fields'] => ({
  ...pickBy(rawFields, field => !field.systemField),
  ...pickBy(rawFields, field => field.systemField),
})

const digestNestedFields = (
  parentField: DigestedObjectFieldWithFields,
  keyPath: string,
): DigestedSchemaField[] =>
  Object.keys(parentField.fields).flatMap(nestedKey => {
    const nestedKeyPath = keyPath + '.' + nestedKey
    const currentFieldLabel = `${parentField.label || parentField.displayName} / ${parentField.fields[nestedKey].displayName}`

    const nestedField = parentField.fields[nestedKey]

    const digestedField: DigestedSchemaField = {
      ...nestedField,
      isDeleted: parentField.isDeleted,
      referencingFieldLabel: parentField.referencingFieldLabel,
      name: nestedKeyPath,
      label: currentFieldLabel,
      systemField: parentField.systemField,
      sortable: nestedField.sortable || false,
      displayName: nestedField.displayName || '',
    }

    if (isOfObjectTypeWithFields(digestedField)) {
      return [
        {
          ...digestedField,
          isNested: true,
        },
        ...digestNestedFields(
          digestedField as DigestedObjectFieldWithFields,
          nestedKeyPath,
        ),
      ]
    } else {
      return [
        {
          ...digestedField,
          isNested: true,
        },
      ]
    }
  })

export const digestSchemaFields = ({
  fields,
  shouldProcessObjectFields = false,
}: {
  fields: { [fieldName: string]: FieldSpec }
  shouldProcessObjectFields?: boolean
}): DigestedSchemaField[] => {
  const processedFields: DigestedSchemaField[] = flatMap(
    fields,
    (fieldDef, fieldName) => {
      const digestedField: DigestedSchemaField = {
        ...fieldDef,
        label: fieldDef.displayName,
        name: fieldName,
        isDeleted: !!fieldDef.isDeleted,
        sortable: get(fieldDef, 'sortable', true),
        systemField: !!fieldDef.systemField,
      }

      if (
        shouldProcessObjectFields &&
        isOfObjectTypeWithFields(digestedField)
      ) {
        const nestedFields = digestNestedFields(
          digestedField as DigestedObjectFieldWithFields,
          fieldName,
        )

        return [digestedField, ...nestedFields]
      }

      return digestedField
    },
  ).filter(field => {
    // When processing object fields, allow nested field names with dots
    // Otherwise, filter out field names containing dots
    if (shouldProcessObjectFields && field.isNested) {
      return true
    }
    return hasLegalFieldname(field)
  })

  return processedFields
}

export const digestSchema = ({
  rawSchema,
  shouldProcessObjectFields = false,
}: {
  rawSchema: Schema
  shouldProcessObjectFields?: boolean
}): DigestedSchema => {
  const isTPACollection = isDbDriverCollection(rawSchema)
  const isAppCollection = getCollectionType(rawSchema) === CollectionType.APP
  const isSinglesCollection = isSinglesCollectionSchema(rawSchema)

  const fields = systemFieldsLast(rawSchema.fields)

  return {
    ...rawSchema,

    name: rawSchema.displayName,

    isTPACollection,
    isAppCollection,
    isSinglesCollection,

    allowedOperations: transformToClientAllowedOperations(
      rawSchema.allowedOperations,
    ),

    defaultSort: rawSchema.defaultSort
      ? deserializeDefaultSort(rawSchema.defaultSort)
      : undefined,

    isDeleted: !!rawSchema.isDeleted,
    fields: digestSchemaFields({
      fields,
      shouldProcessObjectFields,
    }),
  }
}
